"use client";
import Link from "next/link";
import Icon from "@/components/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useIsMobile from "@/hooks/useIsMobile";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import { cn } from "@/lib/utils";
import useTranslations from "@/i18n/useTranslations";
import { useLocale } from "next-intl";
import { formatPromoEndDate } from "@/components/PromoBanner/utils/formatPromoEndDate";

interface PromoBannerTooltipProps {
  htmlContent?: string;
  discountCode?: string;
  endDate?: string;
  learnMoreLink?: string;
  iconColor?: string;
  size?: number;
  className?: string;
}

export default function PromoBannerTooltip({
  htmlContent,
  discountCode,
  endDate,
  learnMoreLink,
  iconColor,
  size = 24,
  className,
}: PromoBannerTooltipProps) {
  const t = useTranslations("Common");
  const locale = useLocale();
  const formattedEndDate = endDate
    ? formatPromoEndDate(endDate, locale)
    : endDate;

  const isMobile = useIsMobile();
  const tooltipIconColor = iconColor ?? "text-primary-foreground";

  const tooltipContent = htmlContent ? (
    sanitizeAndParseInlineHTML(htmlContent, true)
  ) : (
    <>
      {t.rich("promoLegalDisclaimer", {
        date: formattedEndDate ?? "",
        code: discountCode ?? "",
        edxLink: (chunks) => (
          <Link href="/" className="underline">
            {chunks}
          </Link>
        ),
      })}
      {learnMoreLink && (
        <span>
          {" "}
          <a href={learnMoreLink} className="underline">
            {t("learnMore")}.
          </a>
        </span>
      )}
    </>
  );

  return (
    <Popover>
      <PopoverTrigger aria-label={t("moreInformation")}>
        <div className={tooltipIconColor}>
          <Icon type="info" svgProps={{ width: size, height: size }} />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side={isMobile ? "bottom" : "right"}
        className={cn(
          "bg-primary text-primary-foreground rounded-xl",
          className,
        )}
      >
        {tooltipContent}
      </PopoverContent>
    </Popover>
  );
}
